import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border-2 border-ink text-sm font-extrabold cursor-pointer transition-[transform,box-shadow,filter,background-color,color] duration-100 hover:brightness-[1.06] hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-45 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-hard active:translate-y-[5px] active:shadow-none",
        destructive:
          "bg-destructive text-destructive-foreground shadow-hard active:translate-y-[5px] active:shadow-none",
        outline:
          "border-border-soft bg-transparent text-foreground shadow-none hover:bg-secondary/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-hard active:translate-y-[5px] active:shadow-none",
        ghost:
          "border-transparent bg-transparent text-muted-foreground shadow-none hover:bg-secondary/50 hover:text-foreground",
        link: "border-transparent bg-transparent text-primary underline-offset-4 shadow-none hover:underline",
        gradient:
          "bg-[image:var(--gradient-primary)] text-primary-foreground font-display font-normal tracking-[0.04em] shadow-hard active:translate-y-[5px] active:shadow-none",
        gold: "bg-gold text-primary-foreground shadow-hard active:translate-y-[5px] active:shadow-none",
      },
      size: {
        default: "min-h-11 px-4 py-2 sm:min-h-9",
        // 44 px auf dem Handy (min-h-11), erst ab `sm` kleiner. Vorher waren
        // es 40 px und damit unter der Touch-Vorgabe des Design-Kanons — auch
        // auf Knöpfen, die Unwiderrufliches auslösen (Freilassen, Verkaufen).
        sm: "min-h-11 rounded-md px-3 text-xs sm:min-h-8",
        lg: "min-h-11 rounded-md px-8 sm:min-h-10",
        icon: "h-11 w-11 sm:h-9 sm:w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
